import React, { useState } from "react";
import { bulkCreateLeads,bulkSubmitLeads } from "../api/leadService";
import { X } from "lucide-react";

function UploadLeadModal({ isOpen, onClose, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [uploadedLeads, setUploadedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleDownloadTemplate = () => {
    const templateUrl = "/templates/lead_upload_template.xlsx"; 
    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = "lead_upload_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  /** ✅ Upload File and Preview Leads */
  const handleUpload = async () => {
    if (!file) return alert("Please select a file to upload.");

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Send file to API
      const response = await bulkCreateLeads(file);
      console.log("📂 Uploaded Leads:", response);

      if (response?.leads?.length > 0) {
        setUploadedLeads(response.leads);
        alert("✅ File uploaded successfully! Review the leads before submitting.");
      } else {
        alert("⚠️ No leads were processed. Please check your file.");
      }
    } catch (error) {
      console.error("❌ Upload Error:", error);
      alert("Upload failed. Please check the file format.");
    } finally {
      setLoading(false);
    }
  };

  /** ✅ Submit Leads to Backend */
  const handleSubmit = async () => {
    if (uploadedLeads.length === 0) return alert("No leads to submit.");
  
    setSubmitting(true);
    try {
      const response = await bulkSubmitLeads(uploadedLeads);
      console.log("📤 Submit Response:", response);
  
      alert("✅ Leads submitted successfully!");
      onUploadSuccess(uploadedLeads);
      onClose();
    } catch (error) {
      console.error("❌ Submission Error:", error);
      alert("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl">
        
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-100">
          <h2 className="text-xl font-semibold">Upload Leads</h2>
          <button 
            className="text-gray-600 hover:text-gray-900 text-2xl" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* File Upload Section */}
        <div className="p-6 space-y-4">
          <p className="text-gray-600 text-sm">
            Download the <button onClick={handleDownloadTemplate} className="text-blue-600 underline">lead upload template</button>, fill in the required details, and upload the file below.
          </p>
          
          <div className="flex items-center space-x-4">
            <input 
              type="file" 
              accept=".xlsx" 
              onChange={handleFileChange} 
              className="border rounded-md p-2 w-full"
            />
            <button
              onClick={handleUpload}
              disabled={!file || loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500 disabled:bg-gray-400"
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>

        {/* Display Uploaded Leads */}
        {uploadedLeads.length > 0 && (
          <div className="px-6 pb-6">
            <h3 className="text-lg font-semibold mb-2">Uploaded Leads</h3>
            <div className="border rounded-lg overflow-auto max-h-60">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Phone</th>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedLeads.map((lead, index) => (
                    <tr key={index} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">{lead.name}</td>
                      <td className="px-4 py-2">{lead.phone}</td>
                      <td className="px-4 py-2">{lead.email}</td>
                      <td className="px-4 py-2">{lead.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Modal Footer */}
        <div className="flex justify-end space-x-4 px-6 py-4 border-t bg-gray-100">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploadedLeads.length === 0 || submitting}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-500 disabled:bg-gray-400"
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadLeadModal;